"use client";

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { CustomItemInput } from "@/types/inquiry";

interface Props {
    open: boolean;
    onClose: () => void;
    onAdd: (data: CustomItemInput) => void;
}

const EMPTY = { name: "", part: "", brand: "", qty: "1", description: "", link: "" };

/** پنجره‌ی «افزودن محصول یا تجهیز موردنیاز» (درخواست سفارشی) */
export default function CustomItemModal({ open, onClose, onAdd }: Props) {
    const [form, setForm] = useState(EMPTY);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const set = (k: keyof typeof EMPTY) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        const name = form.name.trim();
        if (!name) return setError("نام محصول یا تجهیز الزامی است.");

        let link = form.link.trim();
        if (link && !/^https?:\/\//i.test(link)) link = "https://" + link;
        if (link) {
            try {
                new URL(link);
            } catch {
                return setError("آدرس لینک معتبر نیست.");
            }
        }

        onAdd({
            name,
            part: form.part.trim(),
            brand: form.brand.trim(),
            qty: Math.max(1, parseInt(form.qty, 10) || 1),
            description: form.description.trim(),
            link,
        });
        setForm(EMPTY);
        setError("");
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-[1700] flex items-center justify-center bg-black/50 p-4"
            onMouseDown={(e) => e.target === e.currentTarget && onClose()}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="custom-item-title"
                className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
            >
                <div className="mb-4 flex items-center justify-between">
                    <h2 id="custom-item-title" className="font-bold">
                        افزودن محصول یا تجهیزات موردنیاز
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="بستن"
                        className="rounded p-1 text-gray-500 hover:bg-gray-100"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={submit} className="space-y-3 text-sm">
                    <div>
                        <label className="mb-1 block text-xs text-gray-600">نام محصول / تجهیزات *</label>
                        <Input value={form.name} onChange={set("name")} maxLength={255} autoFocus />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="mb-1 block text-xs text-gray-600">پارت نامبر یا مدل</label>
                            <Input value={form.part} onChange={set("part")} maxLength={150} dir="ltr" />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs text-gray-600">برند</label>
                            <Input value={form.brand} onChange={set("brand")} maxLength={150} />
                        </div>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs text-gray-600">تعداد *</label>
                        <Input
                            type="number"
                            min={1}
                            max={100000}
                            value={form.qty}
                            onChange={set("qty")}
                            dir="ltr"
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs text-gray-600">توضیحات</label>
                        <Textarea rows={2} value={form.description} onChange={set("description")} maxLength={2000} />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs text-gray-600">لینک محصول (در صورت وجود)</label>
                        <Input value={form.link} onChange={set("link")} maxLength={500} dir="ltr" placeholder="https://" />
                    </div>

                    {error && <p role="alert" className="text-xs text-red-600">{error}</p>}

                    <Button type="submit" className="mt-2 w-full">
                        افزودن به سبد
                    </Button>
                </form>
            </div>
        </div>
    );
}
